import React, { forwardRef, useRef } from 'react';
import { motion } from 'framer-motion';
import { MessageCircle, Phone, Globe, Users } from 'lucide-react';
import { AnimatedBeam } from '../ui/magic/AnimatedBeam';
import { cn } from '../../lib/utils';
import curaLogo from '../../assets/logo-only.png';

const Node = forwardRef<
  HTMLDivElement,
  { children: React.ReactNode; label: string; size?: 'md' | 'lg' }
>(({ children, label, size = 'md' }, ref) => (
  <div className="flex flex-col items-center gap-2">
    <div
      ref={ref}
      className={cn(
        'z-10 flex items-center justify-center rounded-full bg-white border border-slate-200 shadow-md overflow-hidden',
        size === 'lg' ? 'w-20 h-20' : 'w-14 h-14'
      )}
    >
      {children}
    </div>
    <span className="text-xs font-semibold tracking-wide text-slate-500 uppercase text-center">
      {label}
    </span>
  </div>
));
Node.displayName = 'Node';

export const HowItWorksSection: React.FC = () => {
  const containerRef = useRef<HTMLDivElement>(null);
  const whatsappRef = useRef<HTMLDivElement>(null);
  const voiceRef = useRef<HTMLDivElement>(null);
  const webRef = useRef<HTMLDivElement>(null);
  const curaRef = useRef<HTMLDivElement>(null);
  const communityRef = useRef<HTMLDivElement>(null);

  return (
    <section className="py-24 bg-white border-t border-slate-200 relative">
      {/* Full height vertical lines at container edges */}
      <div className="absolute inset-0 max-w-7xl mx-auto pointer-events-none">
        <div className="absolute left-0 top-0 bottom-0 w-px bg-slate-200"></div>
        <div className="absolute right-0 top-0 bottom-0 w-px bg-slate-200"></div>
      </div>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-12"
        >
          <h2 className="text-3xl md:text-4xl text-slate-900 font-medium mb-6">
            One Assistant, Every Channel
          </h2>
          <p className="text-lg text-slate-600 font-medium">
            The people you serve reach Cura through WhatsApp, a phone call, or the web,
            and get the same trusted guidance everywhere.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.15 }}
        >
          <div
            ref={containerRef}
            className="relative mx-auto flex w-full max-w-3xl items-center justify-between gap-6 px-2 sm:px-8 py-10"
          >
            <div className="flex flex-col justify-between gap-10 self-stretch">
              <Node ref={whatsappRef} label="WhatsApp">
                <MessageCircle className="w-6 h-6 text-[#003b82]" />
              </Node>
              <Node ref={voiceRef} label="Voice">
                <Phone className="w-6 h-6 text-[#003b82]" />
              </Node>
              <Node ref={webRef} label="Web">
                <Globe className="w-6 h-6 text-[#003b82]" />
              </Node>
            </div>

            <Node ref={curaRef} label="Cura" size="lg">
              <img src={curaLogo} alt="Cura" className="w-full h-full object-cover" />
            </Node>

            <Node ref={communityRef} label="Your Community">
              <Users className="w-6 h-6 text-[#003b82]" />
            </Node>

            <AnimatedBeam
              containerRef={containerRef}
              fromRef={whatsappRef}
              toRef={curaRef}
              curvature={-70}
              endYOffset={-8}
              duration={5}
            />
            <AnimatedBeam
              containerRef={containerRef}
              fromRef={voiceRef}
              toRef={curaRef}
              duration={5}
              delay={1.5}
            />
            <AnimatedBeam
              containerRef={containerRef}
              fromRef={webRef}
              toRef={curaRef}
              curvature={70}
              endYOffset={8}
              duration={5}
              delay={3}
            />
            <AnimatedBeam
              containerRef={containerRef}
              fromRef={curaRef}
              toRef={communityRef}
              duration={5}
              delay={0.75}
            />
          </div>
        </motion.div>
      </div>
    </section>
  );
};
